// OutputTransfer — Set14 P7 §4.11 REVISED (2026-08-17): the CPU-side mirror of the output
// transfer chain the TREE QUAD applies in GLSL (TREE_FRAGMENT_GLSL's `toneMapping()` +
// `linearToOutputTexel()`).
//
// WHY it exists:
//   - The baked atlas is LINEAR (three forces `NoToneMapping` + a linear output colour space for
//     any non-XR render target — `WebGLPrograms.js` — so the bake's MeshStandardMaterial encodes
//     nothing). The map's tree quad therefore applies tone-map + sRGB-encode at the final
//     display write.
//   - The variant PREVIEW strip (SceneryPreviewBaker) does NOT go through the quad shader: it
//     copies atlas pixels into an `ImageData` and `canvas.toDataURL()`s them, which the browser
//     interprets as sRGB. So it must apply the SAME transfer on the CPU, or the preview stays
//     dark and no longer matches the map.
//
// "Can't drift": the preview and the shader can't literally share code (GLSL vs JS), but this
// module is the single source of truth for the JS side, and `OutputTransfer.test.ts` pins a
// known mid-grey round-trip so a silent drift back to raw-linear output fails loudly.
// Note: `LinearToSRGB` is exported from `three/src/math/ColorManagement.js` but NOT re-exported
// by the top-level `three` entry (only the `ColorManagement` object is), so import it directly.
import {LinearToSRGB} from "three/src/math/ColorManagement.js"; // matches three's GLSL sRGBTransferOETF exactly

/** ACESFilmic tone mapping — a faithful port of three r0.174's GLSL
 *  `ACESFilmicToneMapping` (`ShaderChunk/tonemapping_pars_fragment.glsl.js`), exposure 1.
 *  Mirrors the DEFAULT map setting (ToneMappingMode.ACESFilmicToneMapping = 4). */
export function acesFilmicToneMappingJS(color: [number, number, number]): [number, number, number] {
	// const mat3 ACESInputMat (column-major) → rows for M*v: see below.
	let [r, g, b] = color;
	// color *= toneMappingExposure / 0.6  (exposure 1 → /0.6)
	r *= 1 / 0.6; g *= 1 / 0.6; b *= 1 / 0.6;
	// color = ACESInputMat * color
	//   ACESInputMat = mat3(vec3(.59719,.07600,.02840), vec3(.35458,.90834,.13383), vec3(.04823,.01566,.83777))
	//   → rows: (.59719,.35458,.04823) (.07600,.90834,.01566) (.02840,.13383,.83777)
	const ix = 0.59719 * r + 0.35458 * g + 0.04823 * b;
	const iy = 0.07600 * r + 0.90834 * g + 0.01566 * b;
	const iz = 0.02840 * r + 0.13383 * g + 0.83777 * b;
	// color = RRTAndODTFit(color): a/b per channel
	const rrt = (v: number): number => {
		const a = v * (v + 0.0245786) - 0.000090537;
		const b = v * (0.983729 * v + 0.4329510) + 0.238081;
		return a / b;
	};
	const ox = rrt(ix), oy = rrt(iy), oz = rrt(iz);
	// color = ACESOutputMat * color
	//   ACESOutputMat = mat3(vec3(1.60475,-.10208,-.00327), vec3(-.53108,1.10813,-.07276), vec3(-.07367,-.00605,1.07602))
	//   → rows: (1.60475,-.53108,-.07367) (-.10208,1.10813,-.00605) (-.00327,-.07276,1.07602)
	const rx = 1.60475 * ox - 0.53108 * oy - 0.07367 * oz;
	const ry = -0.10208 * ox + 1.10813 * oy - 0.00605 * oz;
	const rz = -0.00327 * ox - 0.07276 * oy + 1.07602 * oz;
	// saturate → clamp to [0,1]
	return [Math.max(0, Math.min(1, rx)), Math.max(0, Math.min(1, ry)), Math.max(0, Math.min(1, rz))];
}

/** Apply the full output transfer chain to a LINEAR RGB triple (the atlas's storage space):
 *  tone-map (ACESFilmic, the default map setting) then sRGB-encode. Mirrors the tree quad's
 *  `#ifdef TONE_MAPPING toneMapping(...) #endif  gl_FragColor = linearToOutputTexel(...)`.
 *  Alpha is NOT touched (the transfer only affects RGB, matching the shader). */
export function applyOutputTransfer(rgb: [number, number, number]): [number, number, number] {
	const [r, g, b] = acesFilmicToneMappingJS(rgb);
	return [LinearToSRGB(r), LinearToSRGB(g), LinearToSRGB(b)];
}
